import { ApiError, NetworkError } from '@/lib/api';
import { Button, cn, Icon, Text, type IconName } from '@/ui';

import type { ReactNode } from 'react';

export interface EmptyStateProps {
  /** `feature` defaults to "search-x"; `inline` and `soft` show no icon unless one is given. */
  icon?: IconName | undefined;
  /** `inline` and `soft` only: the icon's colour. Default the placeholder colour. */
  iconColor?: string | undefined;
  title?: ReactNode | undefined;
  description?: ReactNode | undefined;
  action?: ReactNode | undefined;
  /**
   * - `feature` (default): an info-tinted 48 px icon circle, an h5 title and body-sm text.
   * - `inline`: the quieter list version (design .st-empty): a neutral 44 px icon circle, a
   *   semibold title and a 13 px line, 48 px above and below.
   * - `soft`: `inline` on a grey rounded box with 28 px padding and a 13.5 px line, for
   *   inside a card.
   */
  variant?: 'feature' | 'inline' | 'soft' | undefined;
  className?: string | undefined;
}

/** No data yet, nothing matches, or a feature that is not switched on. Never fake numbers. */
export function EmptyState({
  icon,
  iconColor = 'var(--content-placeholder)',
  title,
  description,
  action,
  variant = 'feature',
  className,
}: EmptyStateProps) {
  if (variant !== 'feature') {
    const soft = variant === 'soft';
    return (
      <div
        className={cn(
          'flex flex-col items-center gap-1.5 text-center',
          soft ? 'rounded-lg bg-surface-static-neutral-default px-6 py-7' : 'px-6 py-12',
          className,
        )}
      >
        {icon ? (
          <span className="mb-1.5 inline-flex size-11 items-center justify-center rounded-full bg-surface-static-neutral-default">
            <Icon name={icon} size={20} color={iconColor} />
          </span>
        ) : null}
        {title ? <div className="font-semibold text-content-default">{title}</div> : null}
        {description ? (
          <div className={cn('text-content-subdued', soft ? 'text-[13.5px]' : 'text-[13px]')}>
            {description}
          </div>
        ) : null}
        {action ? <div className="mt-1">{action}</div> : null}
      </div>
    );
  }
  return (
    <div className={cn('flex flex-col items-center gap-3 px-6 py-12 text-center', className)}>
      <span className="flex size-12 items-center justify-center rounded-full bg-surface-static-info-default">
        <Icon name={icon ?? 'search-x'} size={22} color="var(--surface-static-info-accented)" />
      </span>
      {title ? (
        <Text variant="h5" as="p">
          {title}
        </Text>
      ) : null}
      {description ? (
        <Text variant="body-sm" tone="subdued" className="max-w-[420px]">
          {description}
        </Text>
      ) : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}

export interface ErrorStateProps {
  error: unknown;
  onRetry?: (() => void) | undefined;
  className?: string | undefined;
}

/** A failed request, with the request id so support can find it in the logs. */
export function ErrorState({ error, onRetry, className }: ErrorStateProps) {
  const api = error instanceof ApiError ? error : null;
  const message =
    error instanceof NetworkError
      ? "Couldn't reach the server. Check your connection and try again."
      : api
        ? api.status === 403
          ? "You don't have access to this."
          : api.status === 404
            ? 'This was not found.'
            : api.message
        : 'Something went wrong.';
  return (
    <div role="alert" className={cn('flex flex-col items-center gap-3 px-6 py-12 text-center', className)}>
      <span className="flex size-12 items-center justify-center rounded-full bg-surface-static-danger-default">
        <Icon name="circle-alert" size={22} color="var(--content-danger-default)" />
      </span>
      <Text variant="h5" as="p">
        {message}
      </Text>
      {api?.requestId ? (
        <Text variant="body-micro" tone="placeholder" className="tnum">
          Request {api.requestId}
        </Text>
      ) : null}
      {onRetry ? (
        <Button size="sm" variant="ghost" icon={<Icon name="refresh-cw" size={14} />} onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

/** Shimmering placeholder block (design `.ov-skel`, --radius-sm by default). Give it a width and height with classes. */
export function Skeleton({ className }: { className?: string | undefined }) {
  return <div aria-hidden="true" className={cn('ui-skeleton h-3.5 rounded-sm', className)} />;
}
